import { faFilePen, faMagnifyingGlass, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppBar,
    Autocomplete,
    Button,
    ClickAwayListener,
    Dialog,
    Grid,
    InputAdornment,
    Paper,
    Popper,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { EnumIcon, EnumIconMap, EnumIconValue } from '../../data/IconMap';
import { api } from '../../api';
import { useGlobal } from '../../units/global/GlobalProvider';
import { MenuConfig } from '../../api/menu-config';
import { SimpleAlertProps } from '../../components/SimpleAlert';
import { UserGroup } from '../../api/user-group';

export const useEditMenuConfigDialog = (props: { simpleAlert: SimpleAlertProps }) => {
    const { simpleAlert } = props;
    const classes = useStyles();
    const { userGroups, setUserGroups, reloadUserGroups, menuConfigs, setMenuConfigs, reloadMenuConfigs } = useGlobal();
    const [open, setOpen] = useState(false);
    const [menuConfigId, setMenuConfigId] = useState('');
    const [inputId, setInputId] = useState('');
    const [inputParentMenu, setInputParentMenu] = useState<MenuConfig | null>(null);
    const [inputDescr, setInputDescr] = useState('');
    const [inputNavigateTo, setInputNavigateTo] = useState('');
    const [inputIconValue, setInputIconValue] = useState<EnumIconValue | null>(null);
    const [inputSearchIcon, setInputSearchIcon] = useState('');
    const [showPopper, setShowPopper] = useState(false);
    const [iconOptions, setIconOptions] = useState<EnumIcon[]>([]);
    const [isEnable, setIsEnable] = useState(true);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [originUserGroups, setOriginUserGroups] = useState<UserGroup[]>([]);
    const [inputUserGroups, setInputUserGroups] = useState<UserGroup[]>([]);
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onCreateMenuConfig = async () => {
        await api.menuConfig
            .create({
                id: inputId,
                parentId: inputParentMenu?.id || 'root',
                descr: inputDescr,
                navigateTo: inputNavigateTo,
                iconValue: inputIconValue?.toString() || '',
                enable: isEnable ? 'Y' : 'N',
                userGroups: inputUserGroups,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadMenuConfigs().then(() => reloadUserGroups());
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onUpdateMenuConfig = async () => {
        await api.menuConfig
            .update({
                menuConfigId: menuConfigId,
                id: inputId,
                parentId: inputParentMenu?.id || 'root',
                descr: inputDescr,
                navigateTo: inputNavigateTo,
                iconValue: inputIconValue?.toString() || '',
                enable: isEnable ? 'Y' : 'N',
                userGroups: inputUserGroups,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadMenuConfigs().then(() => reloadUserGroups());
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onReset = () => {
        setMenuConfigId('');
        setInputId('');
        setInputParentMenu(null);
        setInputDescr('');
        setInputNavigateTo('');
        setInputIconValue(null);
        setInputSearchIcon('');
        setShowPopper(false);
        setOriginUserGroups([]);
        setInputUserGroups([]);
    };
    useEffect(() => {
        if (open) {
            reloadUserGroups();
        } else {
            onReset();
        }
    }, [open]);
    useEffect(() => {
        setInputUserGroups(
            userGroups.filter((userGroup) =>
                originUserGroups.map((originUserGroup) => originUserGroup.userGroupId).includes(userGroup.userGroupId!),
            ),
        );
    }, [userGroups]);
    useEffect(() => {
        setInputUserGroups(
            userGroups.filter((userGroup) =>
                originUserGroups.map((originUserGroup) => originUserGroup.userGroupId).includes(userGroup.userGroupId!),
            ),
        );
    }, [originUserGroups, userGroups]);
    useEffect(() => {
        if (inputSearchIcon.length > 0) {
            const searchs = inputSearchIcon.split(/\s+/).filter((v) => v.length > 0);
            const delayResponse = setTimeout(() => {
                setIconOptions(
                    Object.values(EnumIcon)
                        .filter((v, i, a) =>
                            [
                                searchs.length == 0 ? true : searchs.map((s) => v.toUpperCase().includes(s.toUpperCase())).every((v) => v),
                            ].every((v) => v),
                        )
                        .filter((v, i, a) => i < 100),
                );
                setShowPopper(true);
            }, 300);
            return () => clearTimeout(delayResponse);
        } else {
            const delayResponse = setTimeout(() => {
                setIconOptions([]);
                setShowPopper(false);
            }, 300);
            return () => clearTimeout(delayResponse);
        }
    }, [inputSearchIcon]);
    return {
        classes,
        alert,
        open,
        setOpen,
        menuConfigs,
        setMenuConfigs,
        reloadMenuConfigs,
        simpleAlert,
        menuConfigId,
        setMenuConfigId,
        inputId,
        setInputId,
        inputParentMenu,
        setInputParentMenu,
        inputDescr,
        setInputDescr,
        inputNavigateTo,
        setInputNavigateTo,
        inputIconValue,
        setInputIconValue,
        inputSearchIcon,
        setInputSearchIcon,
        showPopper,
        setShowPopper,
        iconOptions,
        setIconOptions,
        isEnable,
        setIsEnable,
        anchorRef,
        originUserGroups,
        setOriginUserGroups,
        inputUserGroups,
        setInputUserGroups,
        userGroups,
        setUserGroups,
        onOpen,
        onClose,
        onCreateMenuConfig,
        onUpdateMenuConfig,
        onReset,
    };
};

export interface EditMenuConfigDialogProps extends ReturnType<typeof useEditMenuConfigDialog> {}

export const EditMenuConfigDialog = (props: EditMenuConfigDialogProps): React.ReactElement => {
    const {
        classes,
        alert,
        open,
        setOpen,
        menuConfigs,
        setMenuConfigs,
        reloadMenuConfigs,
        simpleAlert,
        menuConfigId,
        setMenuConfigId,
        inputId,
        setInputId,
        inputParentMenu,
        setInputParentMenu,
        inputDescr,
        setInputDescr,
        inputNavigateTo,
        setInputNavigateTo,
        inputIconValue,
        setInputIconValue,
        inputSearchIcon,
        setInputSearchIcon,
        showPopper,
        setShowPopper,
        iconOptions,
        setIconOptions,
        isEnable,
        setIsEnable,
        anchorRef,
        originUserGroups,
        setOriginUserGroups,
        inputUserGroups,
        setInputUserGroups,
        userGroups,
        setUserGroups,
        onOpen,
        onClose,
        onCreateMenuConfig,
        onUpdateMenuConfig,
        onReset,
    } = props;
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={open} onClose={onClose}>
            <AppBar position='sticky' className={classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {menuConfigId.length > 0 ? '更新選單' : '新增選單'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                value={inputId}
                                onChange={(e) => setInputId(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'選單ID'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Autocomplete
                                value={inputParentMenu}
                                onChange={(event, newValue) => setInputParentMenu(newValue)}
                                options={menuConfigs}
                                filterOptions={(options, state) => {
                                    const searchs = state.inputValue.split(/\s+/).filter((v) => v.length > 0);
                                    return options
                                        .filter((v, i, a) =>
                                            [
                                                searchs.length == 0
                                                    ? true
                                                    : searchs
                                                          .map(
                                                              (s) =>
                                                                  v?.id?.toUpperCase().includes(s.toUpperCase()) ||
                                                                  v?.descr?.toUpperCase().includes(s.toUpperCase()),
                                                          )
                                                          .every((v) => v),
                                            ].every((v) => v),
                                        )
                                        .filter((v, i, a) => i < 500);
                                }}
                                renderOption={(props, option, state) => (
                                    <li {...props} key={option.menuConfigId!}>{`${option.id}【${option.descr}】`}</li>
                                )}
                                getOptionLabel={(option) => option.id!}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant='outlined'
                                        label={
                                            <>
                                                <span style={{ color: 'red' }}>*</span>
                                                {'父層選單ID'}
                                            </>
                                        }
                                    />
                                )}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                value={inputDescr}
                                onChange={(e) => setInputDescr(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'選單描述'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                value={inputNavigateTo}
                                onChange={(e) => setInputNavigateTo(e.target.value.trim())}
                                variant='outlined'
                                label={'轉跳路徑'}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                value={inputSearchIcon}
                                onChange={(e) => setInputSearchIcon(e.target.value)}
                                variant='outlined'
                                label={'圖示'}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                                onFocus={() => {
                                    inputSearchIcon.length > 0 && setShowPopper(true);
                                }}
                                ref={anchorRef}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment
                                            position='start'
                                            style={{ minWidth: 30, height: '100%', borderRight: '1px solid rgba(0, 0, 0, 0.23)' }}
                                        >
                                            {inputIconValue && (
                                                <Tooltip title={inputIconValue}>
                                                    <FontAwesomeIcon icon={EnumIconMap[inputIconValue]} fixedWidth />
                                                </Tooltip>
                                            )}
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position='start'>
                                            <FontAwesomeIcon icon={faMagnifyingGlass} fixedWidth />
                                            <Button
                                                disabled={![inputIconValue && inputIconValue.length > 0].every((v) => v)}
                                                className={classes.buttonCyan}
                                                style={{ margin: '0px 4px' }}
                                                onClick={() => setInputIconValue(null)}
                                            >
                                                {'取消圖示'}
                                            </Button>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <Stack
                                direction='row'
                                spacing={1}
                                alignItems='center'
                                padding={'0px 8px'}
                                border={'1px solid rgba(0, 0, 0, 0.23)'}
                                borderRadius={1}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            >
                                <Typography>{isEnable ? '啟用' : '禁用'}</Typography>
                                <Switch checked={isEnable} onChange={(e) => setIsEnable(e.target.checked)} />
                            </Stack>
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12}>
                            <Autocomplete
                                multiple
                                value={inputUserGroups}
                                onChange={(event, newValue) => setInputUserGroups(newValue)}
                                options={userGroups}
                                getOptionLabel={(option) => option.descr!}
                                filterSelectedOptions
                                renderInput={(params) => <TextField {...params} label='可使用的使用者群組' placeholder='搜尋使用者群組' />}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <Button
                                disabled={![inputId.length > 0, inputParentMenu != null, inputDescr.length > 0].every((v) => v)}
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={menuConfigId.length > 0 ? onUpdateMenuConfig : onCreateMenuConfig}
                            >
                                <FontAwesomeIcon icon={menuConfigId.length > 0 ? faFilePen : faPlus} fixedWidth />
                                {menuConfigId.length > 0 ? '確定更新' : '確定新增'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Popper open={showPopper} anchorEl={anchorRef.current} style={{ zIndex: 999999 }}>
                <Paper sx={{ width: anchorRef.current?.clientWidth || 400, margin: 1 }}>
                    <ClickAwayListener onClickAway={() => setShowPopper(false)}>
                        <div
                            style={{
                                flex: 1,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr)',
                                gap: 4,
                                padding: 4,
                            }}
                        >
                            {iconOptions.map((v) => (
                                <Grid key={v.toString()} container justifyContent={'center'} alignItems={'center'}>
                                    <ToggleButton
                                        value={v.toString()}
                                        selected={v.toString() == inputIconValue}
                                        onChange={() => {
                                            setInputIconValue(v);
                                            setShowPopper(false);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={EnumIconMap[v]} fixedWidth />
                                    </ToggleButton>
                                </Grid>
                            ))}
                        </div>
                    </ClickAwayListener>
                </Paper>
            </Popper>
        </Dialog>
    );
};
